import React, { useState, useEffect } from 'react';
import '../../styles/Payroll.css';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  LinearProgress,
  Alert,
  Divider,
  IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

import { PayrollMonthService } from '../../services/payrollService';
import { PayrollMonth } from '../../types/payroll';
import { useRole } from '../../hooks/useRole';
import ConfirmDialog from '../common/ConfirmDialog';

const PayrollMonthForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasAnyRole } = useRole();
  
  const [payrollMonth, setPayrollMonth] = useState<PayrollMonth | null>(null);
  const [formData, setFormData] = useState<Partial<PayrollMonth>>({
    status: 'DRAFT',
    period_month: new Date().getMonth() + 1, // 1-indexed month
    period_year: new Date().getFullYear(),
    working_days: 22,
    exchange_rate_usd: 23000,
    payroll_date: new Date().toISOString().split('T')[0],
    cutoff_date: new Date().toISOString().split('T')[0],
  });
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  
  // Ensure user has permission
  const canManagePayrollMonths = hasAnyRole(['payroll', 'admin']);
  const isEditMode = !!id;
  
  // Generate month options
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const date = new Date(2000, i, 1);
    return {
      value: i + 1,
      label: date.toLocaleString('en-US', { month: 'long' })
    };
  });
  
  // Generate year options (current year - 2 to current year + 2)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  
  // Load data if in edit mode
  useEffect(() => {
    const fetchData = async () => {
      if (!isEditMode) return;
      
      try {
        setLoading(true);
        
        // Fetch payroll month
        const response = await PayrollMonthService.getPayrollMonthById(Number(id));
        const month = response.data;
        
        setPayrollMonth(month);
        setFormData({
          period_month: month.period_month,
          period_year: month.period_year,
          working_days: month.working_days,
          exchange_rate_usd: month.exchange_rate_usd,
          payroll_date: month.payroll_date,
          cutoff_date: month.cutoff_date,
          notes: month.notes
        });
        
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load payroll month');
        console.error('Error fetching payroll month:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, isEditMode]);
  
  // Handle field changes
  const handleChange = (field: keyof PayrollMonth, value: any) => {
    setFormData({
      ...formData,
      [field]: value
    });
    
    // Clear error for this field if exists
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: ''
      });
    }
    
    // Clear any success messages when form changes
    if (successMessage) {
      setSuccessMessage(null);
    }
  };
  
  // Handle number input changes
  const handleNumberChange = (field: keyof PayrollMonth, value: string) => {
    const numValue = value === '' ? undefined : Number(value);
    handleChange(field, numValue);
  };
  
  // Generate period label based on month and year
  const generatePeriodLabel = (): string => {
    if (!formData.period_month || !formData.period_year) return '';
    
    const date = new Date(formData.period_year, formData.period_month - 1, 1);
    return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  };
  
  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.period_month) {
      errors.period_month = 'Month is required';
    } else if (formData.period_month < 1 || formData.period_month > 12) {
      errors.period_month = 'Month must be between 1 and 12';
    }
    
    if (!formData.period_year) {
      errors.period_year = 'Year is required';
    }
    
    if (!formData.working_days && formData.working_days !== 0) {
      errors.working_days = 'Working days is required';
    } else if (formData.working_days < 0 || formData.working_days > 31) {
      errors.working_days = 'Working days must be between 0 and 31';
    }
    
    if (!formData.exchange_rate_usd) {
      errors.exchange_rate_usd = 'Exchange rate is required';
    } else if (formData.exchange_rate_usd <= 0) {
      errors.exchange_rate_usd = 'Exchange rate must be positive';
    }
    
    if (!formData.payroll_date) {
      errors.payroll_date = 'Payroll date is required';
    }
    
    if (!formData.cutoff_date) {
      errors.cutoff_date = 'Cutoff date is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Check for existing payroll period
  const checkExistingPeriod = async (): Promise<boolean> => {
    try {
      const response = await PayrollMonthService.getAllPayrollMonths();
      
      const existingPeriods = response.data.filter(pm => {
        // In edit mode, filter out the current month
        if (isEditMode && pm.id === Number(id)) {
          return false;
        }
        
        return pm.period_month === formData.period_month && 
               pm.period_year === formData.period_year;
      });
      
      if (existingPeriods.length > 0) {
        setError(`A payroll period for ${generatePeriodLabel()} already exists.`);
        return false;
      }
      
      return true;
    } catch (err) {
      console.error('Error checking existing periods:', err);
      return false;
    }
  };
  
  // Check if form has been modified
  const isFormChanged = (): boolean => {
    if (!isEditMode) {
      // For new periods, check if any fields were modified from default
      return true; // Assume changes for new forms
    }
    
    // For existing periods, compare with original data
    if (!payrollMonth) return false;
    
    return (
      formData.period_month !== payrollMonth.period_month ||
      formData.period_year !== payrollMonth.period_year ||
      formData.working_days !== payrollMonth.working_days ||
      formData.exchange_rate_usd !== payrollMonth.exchange_rate_usd ||
      formData.payroll_date !== payrollMonth.payroll_date ||
      formData.cutoff_date !== payrollMonth.cutoff_date ||
      formData.notes !== payrollMonth.notes
    );
  };
  
  // Navigate back
  const navigateBack = () => {
    navigate('/payroll/months');
  };
  
  // Handle dialog confirmation
  const handleConfirmDiscard = () => {
    setConfirmDialogOpen(false);
    navigateBack();
  };
  
  const handleCancelDiscard = () => {
    setConfirmDialogOpen(false);
  };
  
  // Handle back
  const handleBack = () => {
    // If form has changes, confirm before navigating away
    if (isFormChanged()) {
      setConfirmDialogOpen(true);
    } else {
      navigateBack();
    }
  };
  
  // Handle save
  const handleSave = async () => {
    if (!canManagePayrollMonths) return;
    
    if (!validateForm()) {
      return;
    }
    
    // For new periods, check if it already exists
    if (!isEditMode) {
      const isValid = await checkExistingPeriod();
      if (!isValid) return;
    }
    
    try {
      setLoading(true);
      
      if (isEditMode) {
        // Update existing period
        await PayrollMonthService.updatePayrollMonth(Number(id), formData);
        setSuccessMessage(`Payroll period for ${generatePeriodLabel()} updated successfully`);
      } else {
        // Create new period
        const response = await PayrollMonthService.createPayrollMonth({
          ...formData,
          status: 'DRAFT' // Always create in DRAFT status
        });
        
        setSuccessMessage(`Payroll period for ${generatePeriodLabel()} created successfully`);
        
        // Navigate to the detail page after successful creation
        setTimeout(() => {
          navigate(`/payroll/months/${response.data.id}`);
        }, 1500);
      }
      
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} payroll period`);
      console.error('Error saving payroll period:', err);
      setSuccessMessage(null);
    } finally {
      setLoading(false);
    }
  };

  // Access denied view
  if (!canManagePayrollMonths) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }} className="payroll-container">
        <Paper sx={{ p: 3 }} className="payroll-card">
          <Typography variant="h5" color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            You do not have permission to manage payroll periods.
          </Typography>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/payroll/months')} 
            sx={{ mt: 2 }}
          >
            Back to Payroll
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} className="payroll-container">
      <Paper sx={{ p: 3 }} className="payroll-card">
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center">
            <IconButton 
              onClick={handleBack} 
              sx={{ mr: 1 }}
              aria-label="back"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" component="h1" className="payroll-card-title">
              {isEditMode ? 'Edit' : 'Create'} Payroll Period
            </Typography>
          </Box>
          
          {/* Status indicators */}
          <Box>
            {loading && <LinearProgress sx={{ width: '100%', mb: 2 }} className="payroll-progress" />}
            
            {error && (
              <div className="payroll-notification payroll-notification-error payroll-notification-mb">
                <div className="payroll-notification-content">
                  {error}
                </div>
              </div>
            )}
            
            {successMessage && (
              <div className="payroll-notification payroll-notification-success payroll-notification-mb">
                <div className="payroll-notification-content">
                  {successMessage}
                </div>
              </div>
            )}
          </Box>
        </Box>
        
        {/* Action buttons */}
        <Box display="flex" justifyContent="flex-end" mb={3}>
          <Button 
            variant="contained" 
            startIcon={<SaveIcon />}
            onClick={handleSave}
            className="payroll-button payroll-button-primary"
          >
            Save
          </Button>
        </Box>
        
        <Box component="form">
          {/* Period Section */}
          <Box mb={4} className="payroll-form-section">
            <Typography variant="h6" gutterBottom className="payroll-section-title">
              Period Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <FormControl fullWidth>
                  <InputLabel id="month-select-label">Month</InputLabel>
                  <Select
                    labelId="month-select-label"
                    label="Month"
                    value={formData.period_month || ''}
                    onChange={(e) => handleChange('period_month', e.target.value)}
                    error={!!formErrors.period_month}
                  >
                    {monthOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.period_month && (
                    <FormHelperText error>{formErrors.period_month}</FormHelperText>
                  )}
                </FormControl>
              </Box>
              
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <FormControl fullWidth>
                  <InputLabel id="year-select-label">Year</InputLabel>
                  <Select
                    labelId="year-select-label"
                    label="Year"
                    value={formData.period_year || ''}
                    onChange={(e) => handleChange('period_year', e.target.value)}
                    error={!!formErrors.period_year}
                  >
                    {yearOptions.map(year => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.period_year && (
                    <FormHelperText error>{formErrors.period_year}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Box>
          </Box>
          
          {/* Work Days & Exchange Rate Section */}
          <Box mb={4} className="payroll-form-section">
            <Typography variant="h6" gutterBottom className="payroll-section-title">
              Financial Settings
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <TextField
                  label="Working Days"
                  type="number"
                  value={formData.working_days || ''}
                  onChange={(e) => handleNumberChange('working_days', e.target.value)}
                  error={!!formErrors.working_days}
                  helperText={formErrors.working_days || 'Standard working days in this period'}
                  fullWidth
                  required
                  InputProps={{
                    inputProps: { min: 0, max: 31, step: 0.5 }
                  }}
                />
              </Box>
              
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <TextField
                  label="Exchange Rate (USD to VND)"
                  type="number"
                  value={formData.exchange_rate_usd || ''}
                  onChange={(e) => handleNumberChange('exchange_rate_usd', e.target.value)}
                  error={!!formErrors.exchange_rate_usd}
                  helperText={formErrors.exchange_rate_usd || 'Exchange rate for USD to VND conversion'}
                  fullWidth
                  required
                  InputProps={{
                    inputProps: { min: 0, step: 100 }
                  }}
                />
              </Box>
            </Box>
          </Box>
          
          {/* Dates Section */}
          <Box mb={4} className="payroll-form-section">
            <Typography variant="h6" gutterBottom className="payroll-section-title">
              Important Dates
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Payroll Date"
                    value={formData.payroll_date ? new Date(formData.payroll_date) : null}
                    onChange={(date) => handleChange('payroll_date', date ? date.toISOString().split('T')[0] : null)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!formErrors.payroll_date,
                        helperText: formErrors.payroll_date || 'Date when payroll is processed'
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
              
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Cutoff Date"
                    value={formData.cutoff_date ? new Date(formData.cutoff_date) : null}
                    onChange={(date) => handleChange('cutoff_date', date ? date.toISOString().split('T')[0] : null)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!formErrors.cutoff_date,
                        helperText: formErrors.cutoff_date || 'Last day for submitting time sheets'
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </Box>
          
          {/* Notes Section */}
          <Box mb={4} className="payroll-form-section">
            <Typography variant="h6" gutterBottom className="payroll-section-title">
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ width: '100%' }}>
              <TextField
                label="Notes"
                value={formData.notes || ''}
                onChange={(e) => handleChange('notes', e.target.value)}
                fullWidth
                multiline
                rows={3}
                helperText="Optional notes about this payroll period"
              />
            </Box>
          </Box>
          
          {/* Metadata Section (edit mode only) */}
          {isEditMode && payrollMonth && (
            <Box mb={4} className="payroll-form-section">
              <Typography variant="h6" gutterBottom className="payroll-section-title">
                Metadata
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <TextField
                    label="Status"
                    value={payrollMonth.status || 'DRAFT'}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Box>
                
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <TextField
                    label="Last Updated"
                    value={payrollMonth.updated_at ? new Date(payrollMonth.updated_at).toLocaleString() : 'Not available'}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Box>
              </Box>
            </Box>
          )}
          
          <Box display="flex" justifyContent="flex-end" mt={4} className="payroll-form-actions">
            <Button 
              variant="outlined" 
              onClick={handleBack} 
              sx={{ mr: 1 }}
              className="payroll-button payroll-button-secondary"
            >
              Cancel
            </Button>
            
            <Button 
              variant="contained" 
              onClick={handleSave}
              disabled={loading}
              startIcon={<SaveIcon />}
              className="payroll-button payroll-button-primary"
            >
              {isEditMode ? 'Update' : 'Create'} Payroll Period
            </Button>
          </Box>
        </Box>
      </Paper>
      
      {/* Confirmation Dialog for Unsaved Changes */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Discard Changes"
        content="You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost."
        onConfirm={handleConfirmDiscard}
        onCancel={handleCancelDiscard}
        confirmText="Discard Changes"
        confirmColor="error"
      />
    </Container>
  );
};

export default PayrollMonthForm;
